import type { Dayjs } from '@vartion/ui'
import localforage from 'localforage'

import type { AsyncCache } from '~/types.ts'
import { type CacheItem, createInstance, createKey, createValue, getValue, isExpired } from './helpers.ts'

export class IndexedDBCache implements AsyncCache {
  name: string
  cache: typeof localforage

  constructor({ name }: { name: string }) {
    this.name = name
    this.cache = localforage.createInstance({ name })
    setInterval(this.flushExpired.bind(this), 60000)
  }

  static tags(tags: string | string[]): IndexedDBCache {
    return createInstance('IndexedDBCache', tags, IndexedDBCache) as IndexedDBCache
  }

  async has(key: unknown) {
    return (await this.get(key)) !== null
  }

  async get<T = any, D = null>(key: unknown, defaultValue: D = null as D): Promise<T | D> {
    const cacheKey = createKey(key)

    const itemString = await this.cache.getItem<string>(cacheKey)
    if (!itemString) return defaultValue

    const item = itemString.split(':') as CacheItem

    if (isExpired(item)) {
      await this.cache.removeItem(cacheKey)

      return defaultValue
    }

    return getValue(item) as T
  }

  async put(key: unknown, value: unknown, date = dayjs().add(1, 'year')) {
    await this.store(createKey(key), createValue(value, date))
  }

  async remember<T = any>(key: unknown, duration: Dayjs, callback: () => T | Promise<T>): Promise<T> {
    let value = await this.get(key)

    if (value !== null) {
      return value
    }

    value = await callback()

    await this.put(key, value, duration)

    return value
  }

  async forget(key: string): Promise<void> {
    await this.cache.removeItem(createKey(key))
  }

  async list() {
    const list: Record<string, string> = {}

    for (const key of await this.cache.keys()) {
      list[key] = (await this.cache.getItem(key)) as string
    }

    return list
  }

  async flush() {
    for (const key of await this.cache.keys()) {
      await this.cache.removeItem(key)
    }
  }

  async flushExpired() {
    for (const key of await this.cache.keys()) {
      const itemString = (await this.cache.getItem(key)) as string
      if (!itemString) continue

      const item = itemString.split(':') as CacheItem

      if (isExpired(item)) {
        await this.cache.removeItem(key)
      }
    }
  }

  protected async store(key: string, value: string) {
    await this.cache.setItem(key, value)
  }
}
